import * as Html from "../util/Html.ts";
import * as actionMenu from  "../widget/action-menu-registry.ts"
import { datePicker, quantityPicker } from "../widget/action-menu-content.ts";
import * as i18n from "../i18n.ts";
import { parseLeadingQuantity } from "../lib/ingredientParser.ts";
import { formatQuantityPrefix } from "../lib/unitFormat.ts";
import {TaskListCache} from "../server/objects/TaskBeans.ts"
import { getEndOfDay } from "../lib/DateUtils.ts";
import * as modal from "../util/modal.ts";
import { TaskInputBean } from "../server/fizapilib.auto.ts";
import * as CustomInput from "../util/CustomInput.ts";

/** The quantity icon is branded whenever the typed line carries a quantity, typed or picked. */
function markQuantity(icon :HTMLElement, input :HTMLInputElement){
  icon.classList.toggle('color-branded',!!parseLeadingQuantity(input.value,i18n.getShortLanguage()));
}

actionMenu.register({
  taskduedatepick(){
    var
      node = this,
      input = this.closest('form').dueDate;
      
    return {
      content:datePicker({
        date:CustomInput.getDateValue(input)||new Date(),
        onpickdate(date){
          var endOfDay = getEndOfDay(date);
          input.value = new Date(endOfDay.getTime()-3600000).toISOString();
          node.classList.add('color-branded')
        }
      })
    }
  },
  taskquantitypick(){
    const
      node = this,
      input = (this.closest('form') as QuickAddForm).text,
      //the typed line is the single source of truth: the picker edits its head, nothing is hidden
      opened = parseLeadingQuantity(input.value,i18n.getShortLanguage()),
      content = quantityPicker({quantity:opened?.quantity,unit:opened?.unit});

    return {
      content,
      //rewriting on every click would make the line jump around while the user is still stepping
      onCancel(){
        const
          {quantity,unit} = content.value(),
          //the input keeps the focus, so the item name may have grown since the menu was opened
          current = parseLeadingQuantity(input.value,i18n.getShortLanguage()),
          name = current?current.name:input.value,
          prefix = formatQuantityPrefix(quantity,unit);
        
        input.value = prefix+' '+name;
        markQuantity(node,input);
        //the user was typing the item when they reached for the quantity, give the keyboard back
        input.focus();
        input.setSelectionRange(input.value.length,input.value.length);
      }
    }
  },
  taskcategorypick(){
    var
      node = this,
      metaId = Html.getDomMetaId(this),
      input = this.closest('form').taskCategoryId,
      categories = TaskListCache.get(metaId).getCategories(true),
      buttons :actionMenu.ActionMenuButton[] = [{
        clickFunction(){
          input.value = '';
          node.classList.remove('color-branded')
        },
        emoji:'🏷️',
        selected:''==input.value,
        i18nkey:'catsystem.uncategorized'
      }];

    categories.forEach(category=>{
      buttons.push({
        clickFunction(){
          input.value = category.metaId;
          node.classList.add('color-branded')
        },
        emoji:category.emoji,
        selected:category.metaId==input.value,
        i18nkey:category.name
      })
    });

    return {
      menu:buttons
    }
  },
  taskassigneepick(){
    var
      node = this,
      input = this.closest('form').assignee,
      metaId = Html.getDomMetaId(this),
      members = TaskListCache.get(metaId).members.filter(m=>m.shared),
      buttons = [];

    members.forEach(member=>{
      buttons.push({
        clickFunction(){
          input.value = member.accountId;
          node.classList.add('color-branded')
        },
        iconclass:"fw-avatar fw-avatar-sizeXS",
        img:member.medias[0].pictureUrl,
        selected:member.accountId==input.value, // == because one is Long the other is String
        title:member.name
      })
    });

    return {'menu':buttons}
  }
});

type QuickAddForm = HTMLFormElement & {
  taskListId : HTMLInputElement
  assignee : HTMLInputElement
  taskCategoryId : HTMLInputElement
  dueDate : HTMLInputElement
  text : HTMLInputElement
  picture : HTMLInputElement
}
Html.registerDom({
  async taskquickaddswitchfull(){
    const
      taskListId = Html.getDomMetaId(this),
      form = this.closest('form') as QuickAddForm,
      TPL = await import("../list/task_edit.mustache"),
      assignee = form.assignee.value,
      taskCategoryId = form.taskCategoryId.value,
      dueDate = form.dueDate.value==='$empty'?undefined:form.dueDate.value,
      customFiles = form.picture.customFiles,
      //the full form has its own quantity fields: the head of the typed line belongs to them, not to
      //the item name it would otherwise be saved as
      quantityOf = TaskListCache.get(taskListId).taskListType==='SHOPPING_LIST'
        ?parseLeadingQuantity(form.text.value,i18n.getShortLanguage())
        :null,

      task = new TaskInputBean({
        text:quantityOf?.name || form.text.value,
        //the bean is built from its json form, where a number travels as a string
        quantity:quantityOf?String(quantityOf.quantity):undefined,
        unit:quantityOf?.unit,
        recurrency:'NONE',
        dueDate,
        taskCategoryId,
        taskListId
      });
    const
      theModal = modal.openDomModal(TPL,{task});

    setTimeout(()=>{
      if(customFiles){
        const newInputFile = theModal.querySelector('[name=picture]') as HTMLInputElement;

        newInputFile.customFiles = customFiles
        newInputFile.dispatchEvent(new Event('change'));
      }
      if(assignee!=='$empty'){
        theModal.querySelector(`[data-accountid="${assignee}"]`)?.dispatchEvent(new Event('click'));
      }
    },100)



  }
})


Html.registerWidget('list/taskQuickAdd',{initDom})

function initDom(dom : HTMLElement){
  var
    input = dom.querySelector('input[name="text"]') as HTMLInputElement,
    quantityIcon = dom.querySelector('.fw-quickadd-quantity') as HTMLElement;


  input.addEventListener('focus',function(){
    dom.querySelectorAll<HTMLElement>('icon').forEach(icon=>icon.style.display='block')
  })
  //typing "300g milk" by hand must brand the icon just like picking the quantity does
  if(quantityIcon){
    input.addEventListener('input',()=>markQuantity(quantityIcon,input))
  }
  //a reset does not restore the hidden fields fed by the pickers: setting their value rewrites their
  //default too, so the next line would inherit the due date, assignee or category of the previous one
  const hiddenDefaults = Array.from(dom.querySelectorAll<HTMLInputElement>('input[type="hidden"]')).map(input=>({input,value:input.value}));
  //the pickers highlight their icon once fed, resetting the form has to clear that too
  dom.addEventListener('reset',function(){
    dom.querySelectorAll('icon.color-branded').forEach(icon=>icon.classList.remove('color-branded'))
    hiddenDefaults.forEach(({input,value})=>input.value=value)
  })
}
